import { Globe, Heart, Syringe, Activity, Shield, CheckCircle2, Stethoscope, Brain, Accessibility } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const PHOTO_URL = "https://customer-assets.emergentagent.com/job_f7441d13-fe77-47a4-8940-8adf7bec2132/artifacts/n8ugmuq5_WhatsApp%20Image%202026-02-26%20at%2017.01.48.jpeg";

const AboutPhoto = () => (
  <div className="relative">
    <div className="rounded-[2rem] overflow-hidden shadow-xl">
      <img
        src={PHOTO_URL}
        alt="Yahaira Caceres Zamora - Professional Caregiver"
        className="w-full h-[550px] object-cover object-top"
        data-testid="about-image"
      />
    </div>
    <div className="absolute -bottom-8 right-8 bg-gradient-to-br from-[#003A4B] to-[#1B5E6B] rounded-2xl p-6 shadow-xl text-white">
      <div className="text-center">
        <p className="text-4xl font-bold">10+</p>
        <p className="text-white/80">Years caring for patients</p>
      </div>
    </div>
  </div>
);

const DementiaCard = ({ t }) => (
  <div data-testid="dementia-experience-card" className="bg-gradient-to-r from-[#003A4B] to-[#1B5E6B] rounded-2xl p-6 text-white shadow-xl">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0">
        <Brain className="w-6 h-6 text-[#C89D4B]" />
      </div>
      <div>
        <h4 className="font-semibold mb-2">{t.about.dementiaTitle}</h4>
        <p className="text-white/85 leading-relaxed text-sm">{t.about.dementiaText}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-xs font-medium">
            <Brain className="w-3 h-3 text-[#C89D4B]" />{t.about.dementiaBadge1}
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-xs font-medium">
            <Accessibility className="w-3 h-3 text-[#C89D4B]" />{t.about.dementiaBadge2}
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-xs font-medium">
            <Heart className="w-3 h-3 text-[#C89D4B]" />{t.about.dementiaBadge3}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const VocationCard = ({ t }) => (
  <div className="bg-gradient-to-r from-[#F3F0E7] to-[#F8F5EE] rounded-2xl p-6 border-l-4 border-[#C89D4B]">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 bg-[#C89D4B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
        <Heart className="w-6 h-6 text-[#C89D4B]" />
      </div>
      <div>
        <h4 className="font-semibold text-[#1A2332] mb-2">{t.about.vocationTitle}</h4>
        <p className="text-[#4A5568] leading-relaxed">{t.about.vocationText}</p>
      </div>
    </div>
  </div>
);

const LanguageCard = ({ t }) => (
  <div className="bg-[#F3F0E7]/60 rounded-2xl p-5 border border-[#003A4B]/10">
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 bg-[#003A4B]/10 rounded-xl flex items-center justify-center flex-shrink-0">
        <Globe className="w-5 h-5 text-[#003A4B]" />
      </div>
      <div>
        <h4 className="font-semibold text-[#1A2332] mb-1 text-sm">{t.about.languageTitle}</h4>
        <p className="text-[#4A5568] text-sm">{t.about.languageText}</p>
      </div>
    </div>
  </div>
);

const CertificationsGrid = ({ certifications }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
    {certifications.map((cert) => (
      <div
        key={cert.label}
        className="flex items-center gap-3 p-3 bg-white rounded-xl border border-[#003A4B]/10 hover:border-[#C89D4B]/40 hover:shadow-md transition-all"
      >
        <cert.icon className="w-5 h-5 text-[#003A4B] flex-shrink-0" />
        <span className="text-[#1A2332] font-medium text-sm">{cert.label}</span>
      </div>
    ))}
  </div>
);

export const AboutSection = () => {
  const { t } = useLanguage();

  const certifications = [
    { icon: Syringe, label: t.about.phlebotomist },
    { icon: Activity, label: t.about.ekg },
    { icon: Shield, label: t.about.firstAid },
    { icon: Heart, label: t.about.cprAed },
    { icon: Stethoscope, label: t.about.stopBleeding },
    { icon: CheckCircle2, label: t.about.hipaa },
  ];

  return (
    <section id="about" data-testid="about-section" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <AboutPhoto />
          <div className="space-y-6">
            <span className="text-[#C89D4B] font-semibold uppercase tracking-widest text-sm">{t.about.sectionLabel}</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A2332]">{t.about.name}</h2>
            <p className="text-[#4A5568] leading-relaxed text-lg">{t.about.description}</p>
            <DementiaCard t={t} />
            <VocationCard t={t} />
            <LanguageCard t={t} />
            <div>
              <h4 className="font-semibold text-[#1A2332] mb-4">{t.about.certifications}</h4>
              <CertificationsGrid certifications={certifications} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
